import { useCallback, useEffect, useState } from "react";
import { Check, Copy, KeyRound, Loader2, Pencil, Plus, ShieldCheck, UserCog, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { MfaSelfService } from "@/components/settings/MfaSelfService";
import { OverviewLayoutSettings } from "@/components/settings/OverviewLayoutSettings";
import { CronHealth, CronFailureBanner } from "@/components/settings/CronHealth";
import { BrandingSettings } from "@/components/settings/BrandingSettings";
import { TenantPolicySettings } from "@/components/settings/TenantPolicySettings";
import { PermissionsManager } from "@/components/settings/PermissionsManager";
import { AccessReview, dormancy, sinceLabel } from "@/components/settings/AccessReview";
import { LoginAlerts } from "@/components/settings/LoginAlerts";
import { MessagingSettings } from "@/components/settings/MessagingSettings";
import { DonorPortalSettings } from "@/components/settings/DonorPortalSettings";
import { PaymentSettings } from "@/components/settings/PaymentSettings";
import { SettingsChanges } from "@/components/common/SettingsChanges";
import { NotificationSettings } from "@/components/settings/NotificationSettings";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const BLANK = { email: "", name: "", roles: [], mfa_enabled: false };

export default function Rbac() {
  const { can, user: me } = useAuth();
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const roles = await api.get("/admin/roles");
    setMeta(roles.data);
    if (can("user", "read")) {
      const u = await api.get("/admin/users");
      setUsers(u.data);
    }
  }, [can]);

  useEffect(() => { load().catch(() => setMeta(false)); }, [load]);

  const provision = async () => {
    setBusy("save");
    try {
      const { data } = await api.post("/admin/users", {
        email: form.email, name: form.name, role: form.roles[0], roles: form.roles,
        mfa_enabled: form.mfa_enabled,
      });
      setOpen(false);
      setForm(BLANK);
      setIssued(data);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const saveEdit = async () => {
    setBusy("edit");
    try {
      await api.patch(`/admin/users/${edit.id}`, {
        name: edit.name, email: edit.email, status: edit.status, roles: edit.roles,
      });
      toast.success(t("Their details are updated"));
      setEdit(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const reissue = async (u) => {
    setBusy(`temp-${u.id}`);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/temp-password`);
      setIssued(data);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const toggleMfa = async (u) => {
    const enable = !(u.mfa_enforced || u.mfa_enrolled);
    setBusy(`mfa-${u.id}`);
    try {
      await api.post(`/admin/users/${u.id}/mfa`, { enabled: enable });
      toast.success(enable
        ? t("{{name}} will be asked for a one-time code at next sign-in", { name: u.name })
        : t("Two-factor requirement removed for {{name}}", { name: u.name }));
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!meta) return <Loading label={i18n.t("Loading roles and permissions")} />;

  const roleLabel = (key) => meta.roles.find((r) => r.key === key)?.label || key;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("SCR-017 · Admin & RBAC")}
        title={t("Least privilege, enforced server-side")}
        description={t("Every role is mapped to the screens it may read and change and to the powers it holds. Each protected request is authorised on the server; maker-checker separations are enforced in the rule and transaction lifecycles.")}
        actions={can("user", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-user-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> {t("Provision user")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md border-border bg-card" data-testid="new-user-dialog">
              <DialogHeader>
                <DialogTitle>{t("Provision a user")}</DialogTitle>
                <DialogDescription>
                  {t("We issue a temporary password and show it to you once. They set their own the first time they sign in.")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <F label={t("Full name")}>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                         data-testid="user-name-input" className="bg-muted/40" />
                </F>
                <F label={t("Work email")}>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                         data-testid="user-email-input" className="bg-muted/40 font-mono text-sm" />
                </F>
                <F label={t("Roles — tick as many hats as they wear")}>
                  <RolePicker roles={meta.roles} picked={form.roles}
                              onChange={(roles) => setForm({ ...form, roles })} />
                </F>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{t("Require MFA (privileged role)")}</span>
                  <Switch checked={form.mfa_enabled} onCheckedChange={(v) => setForm({ ...form, mfa_enabled: v })}
                          data-testid="user-mfa-toggle" />
                </div>
              </div>
              <Button onClick={provision}
                      disabled={busy === "save" || !form.email || !form.name || !form.roles.length}
                      data-testid="user-save-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("Provision")}
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <CronFailureBanner />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-roles" label={t("Roles")} value={meta.roles.length} accent="sky" icon={UserCog} />
        {can("user", "read") && (
          <>
            <Kpi testId="kpi-users" label={t("Provisioned users")} value={users.length} accent="violet" />
            <Kpi testId="kpi-mfa" label={t("MFA enforced")} accent="emerald" icon={ShieldCheck}
                 value={users.filter((u) => u.mfa_enforced || u.mfa_enrolled).length} />
          </>
        )}
        <Kpi testId="kpi-resources" label={t("Protected resources")} value={meta.resources.length} accent="amber" />
      </div>

      <Tabs defaultValue="permissions">
        <TabsList>
          <TabsTrigger value="permissions" data-testid="tab-permissions">{t("Permissions")}</TabsTrigger>
          <TabsTrigger value="policy" data-testid="tab-tenant-policy">{t("Tenant policy")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">{t("Users")}</TabsTrigger>
          {can("user", "write") && (
            <TabsTrigger value="messaging" data-testid="tab-messaging">{t("Messaging")}</TabsTrigger>
          )}
          <TabsTrigger value="portal" data-testid="tab-donor-portal">{t("Donor portal")}</TabsTrigger>
          {can("payment", "read") && (
            <TabsTrigger value="payments" data-testid="tab-payments">{t("Payments")}</TabsTrigger>
          )}
          <TabsTrigger value="changes" data-testid="tab-settings-changes">{t("Pending changes")}</TabsTrigger>
          <TabsTrigger value="notify" data-testid="tab-notifications">{t("Notifications")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">{t("My security")}</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview-layout">{t("Overview layout")}</TabsTrigger>
          {can("user", "read") && (
            <TabsTrigger value="jobs" data-testid="tab-cron-health">{t("Scheduled jobs")}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="permissions" className="mt-4">
          <PermissionsManager />
        </TabsContent>

        <TabsContent value="policy" className="mt-4">
          <TenantPolicySettings />
        </TabsContent>

        <TabsContent value="users" className="mt-4 space-y-4">
          {can("user", "read") && (
            <AccessReview canWrite={can("user", "write")} onChanged={load} />
          )}
          <Panel testId="panel-users" kicker={t("Identity")} title={t("Provisioned users")}>
            {!can("user", "read") && (
              <div className="p-6 text-xs text-muted-foreground" data-testid="users-forbidden">
                {t("Your role does not include read access to user administration.")}
              </div>
            )}
            {can("user", "read") && (
              <Table testId="users-table"
                     head={<><Th>{t("User")}</Th><Th>{t("Roles")}</Th><Th>{t("MFA")}</Th><Th>{t("Status")}</Th><Th>{t("Last login")}</Th><Th /></>}>
                {users.length === 0 && <Empty colSpan={6} />}
                {users.map((u) => (
                  <tr key={u.id} className="row-hover" data-testid={`user-row-${u.email}`}>
                    <Td>
                      <div className="font-medium text-foreground">{u.name}</div>
                      <div className="font-mono text-[11px] text-muted-foreground">{u.email}</div>
                      {u.must_change_password && (
                        <Chip testId={`user-temp-${u.email}`}
                              className="mt-1 border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                          {t("on a temporary password")}
                        </Chip>
                      )}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1">
                        {(u.roles?.length ? u.roles : [u.role]).map((key, i) => (
                          <Chip key={key} testId={`user-role-${u.email}-${key}`}
                                className={i === 0
                                  ? "border-primary/40 bg-primary/10 text-primary"
                                  : "border-border bg-muted text-muted-foreground"}>
                            {roleLabel(key)}
                          </Chip>
                        ))}
                      </div>
                    </Td>
                    <Td data-testid={`user-mfa-cell-${u.email}`}>
                      {can("user", "write") ? (
                        <div className="flex items-center gap-2">
                          <Switch checked={Boolean(u.mfa_enforced || u.mfa_enrolled)}
                                  onCheckedChange={() => toggleMfa(u)}
                                  disabled={busy === `mfa-${u.id}`}
                                  data-testid={`user-mfa-switch-${u.email}`} />
                          <span className="text-[10px] text-muted-foreground">
                            {t(u.mfa_enrolled ? "enrolled" : u.mfa_enforced ? "pending enrolment" : "off")}
                          </span>
                        </div>
                      ) : (
                        u.mfa_enrolled || u.mfa_enforced
                          ? <Check className="h-3.5 w-3.5 text-emerald-500" />
                          : <X className="h-3.5 w-3.5 text-muted-foreground/50" />
                      )}
                    </Td>
                    <Td>
                      <Chip testId={`user-status-${u.email}`} className={u.status === "active"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>{t(u.status)}</Chip>
                    </Td>
                    <Td className="font-mono text-[11px]" data-testid={`user-last-login-${u.email}`}>
                      {u.last_login_at ? dateTime(u.last_login_at) : t("never")}
                      <span className={`mt-0.5 block font-sans text-[10px] ${
                        (dormancy(u) === null || dormancy(u) >= 90)
                          ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}
                            data-testid={`user-days-since-${u.email}`}>
                        {sinceLabel(dormancy(u))}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex justify-end gap-1.5">
                        {can("user", "write") && u.must_change_password && !u.temp_password_used && (
                          <Button size="sm" variant="ghost" className="h-7 rounded-full px-2.5 text-[10px]"
                                  data-testid={`reissue-temp-${u.email}`}
                                  disabled={busy === `temp-${u.id}`}
                                  onClick={() => reissue(u)}>
                            {busy === `temp-${u.id}` ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <KeyRound className="h-3 w-3" />}
                          </Button>
                        )}
                        {can("user", "write") && (
                          <Button size="sm" variant="outline"
                                  data-testid={`edit-user-${u.email}`}
                                  onClick={() => setEdit({
                                    id: u.id, name: u.name, email: u.email, status: u.status,
                                    roles: u.roles?.length ? [...u.roles] : [u.role],
                                    self: u.email === me?.email,
                                  })}
                                  className="h-7 rounded-full border-border px-2.5 text-[10px]">
                            <Pencil className="mr-1 h-3 w-3" /> {t("Edit")}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>
        </TabsContent>

        <TabsContent value="security" className="mt-4 space-y-4">
          <MfaSelfService />
          <LoginAlerts canRead={can("user", "read")} canWrite={can("user", "write")} />
        </TabsContent>

        <TabsContent value="messaging" className="mt-4">
          <MessagingSettings />
        </TabsContent>

        <TabsContent value="portal" className="mt-4">
          <DonorPortalSettings />
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <PaymentSettings />
        </TabsContent>

        <TabsContent value="changes" className="mt-4">
          <SettingsChanges />
        </TabsContent>

        <TabsContent value="notify" className="mt-4">
          <NotificationSettings />
        </TabsContent>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <OverviewLayoutSettings />
          <BrandingSettings />
        </TabsContent>

        <TabsContent value="jobs" className="mt-4">
          <CronHealth />
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(edit)} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>{t("Edit {{name}}", { name: edit?.name })}</DialogTitle>
            <DialogDescription>
              {t("Their details, their sign-in email and every hat they wear. The first role ticked is their primary one, used on labels and in the audit trail.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label={t("Full name")}>
              <Input value={edit?.name || ""} data-testid="edit-user-name"
                     onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                     className="bg-muted/40" />
            </F>
            <F label={t("Sign-in email")}>
              <Input value={edit?.email || ""} data-testid="edit-user-email"
                     onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                     className="bg-muted/40 font-mono text-sm" />
            </F>
            <F label={t("Status")}>
              <Select value={edit?.status || "active"} disabled={edit?.self}
                      onValueChange={(v) => setEdit({ ...edit, status: v })}>
                <SelectTrigger data-testid="edit-user-status" className="bg-muted/40">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t("Active")}</SelectItem>
                  <SelectItem value="disabled">{t("Disabled")}</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label={t("Roles")}>
              <RolePicker roles={meta.roles} picked={edit?.roles || []}
                          onChange={(roles) => setEdit({ ...edit, roles })} testPrefix="edit-role" />
            </F>
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-user-save"
                    disabled={busy === "edit" || !(edit?.roles || []).length || !edit?.name
                      || !edit?.email}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {busy === "edit" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("Save changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(issued)} onOpenChange={(o) => !o && setIssued(null)}>
        <DialogContent className="max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>{t("Temporary password")}</DialogTitle>
            <DialogDescription>
              {t("Give this to {{name}} yourself. This is the only time it is shown — it is stored one-way, so nobody can look it up afterwards.", { name: issued?.user?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-4 text-center">
            <p className="font-mono text-lg tracking-wide text-foreground"
               data-testid="temp-password-value">{issued?.temporary_password}</p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {issued?.user?.email}
            </p>
          </div>
          <Button variant="outline" data-testid="temp-password-copy"
                  onClick={() => {
                    navigator.clipboard?.writeText(issued.temporary_password);
                    toast.success(t("Copied"));
                  }}
                  className="rounded-full border-border">
            <Copy className="mr-1.5 h-3.5 w-3.5" /> {t("Copy it")}
          </Button>
          <p className="text-[11.5px] leading-relaxed text-muted-foreground"
             data-testid="temp-password-note">
            {t("Good for {{h}} hours, until {{when}}. They will be asked to set their own password the first time they sign in. While it is still unused you can issue a fresh one from their row.", { h: issued?.valid_hours, when: dateTime(issued?.expires_at) })}
          </p>
          <DialogFooter>
            <Button onClick={() => setIssued(null)} data-testid="temp-password-done"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              {t("I have written it down")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const RolePicker = ({ roles, picked, onChange, testPrefix = "role" }) => (
  <div className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-border p-1.5"
       data-testid={`${testPrefix}-picker`}>
    {roles.map((r) => {
      const held = picked.includes(r.key);
      return (
        <label key={r.key}
               className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted/50">
          <Checkbox checked={held} data-testid={`${testPrefix}-toggle-${r.key}`}
                    onCheckedChange={() => onChange(held
                      ? picked.filter((x) => x !== r.key)
                      : [...picked, r.key])} />
          <span className="flex-1 text-[12.5px] text-foreground">{r.label}</span>
          {picked[0] === r.key && (
            <Chip className="border-primary/40 bg-primary/10 text-primary">{i18n.t("primary")}</Chip>
          )}
          {r.custom && (
            <Chip className="border-border bg-muted text-muted-foreground">{i18n.t("ours")}</Chip>
          )}
        </label>
      );
    })}
  </div>
);

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
